import { useMemo, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactECharts from "echarts-for-react";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

interface ChartNode {
  userId: string; name: string; email: string;
  cargo: string | null; rank: number | null; departamento: string | null;
  superiorId: string | null; subordinates: ChartNode[];
}
interface Membro {
  userId: string; name: string; email: string; isAdmin: boolean;
  node: { id: string; superiorId: string | null; cargo: { id: string; name: string } | null; departamento: { id: string; name: string } | null } | null;
}
interface Cargo { id: string; name: string; rank: number }
interface Departamento { id: string; name: string }

// Converte a floresta numa árvore ECharts sob a instituição como ápice.
function toTree(n: ChartNode): Record<string, unknown> {
  return {
    name: `${n.name}\n${n.cargo ?? "—"}`,
    _depto: n.departamento,
    children: n.subordinates.map(toTree),
  };
}

export function OrganogramaPage() {
  const { user, tenants } = useAuth();
  const qc = useQueryClient();
  const [pickInst, setPickInst] = useState<string>("");
  const inst = pickInst || user?.selectedTenantId || (tenants[0]?.id ?? "");
  const instName = tenants.find((t) => t.id === inst)?.name ?? "Instituição";
  const canEdit = !!user?.isMasterAdmin || (user?.adminInstitutionIds ?? []).includes(inst);

  const chartQ = useQuery({
    queryKey: ["org-chart", inst],
    queryFn: async () => (await api.get<{ roots: ChartNode[]; unplaced: { userId: string; name: string }[] }>(`/institutions/${inst}/org-chart`)).data,
    enabled: !!inst,
  });
  const membrosQ = useQuery({
    queryKey: ["org-membros", inst],
    queryFn: async () => (await api.get<Membro[]>(`/institutions/${inst}/membros`)).data,
    enabled: !!inst && canEdit,
  });
  const cargosQ = useQuery({
    queryKey: ["org-cargos", inst],
    queryFn: async () => (await api.get<Cargo[]>(`/institutions/${inst}/cargos`)).data,
    enabled: !!inst && canEdit,
  });
  const deptosQ = useQuery({
    queryKey: ["org-deptos", inst],
    queryFn: async () => (await api.get<Departamento[]>(`/institutions/${inst}/departamentos`)).data,
    enabled: !!inst && canEdit,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["org-chart", inst] });
    qc.invalidateQueries({ queryKey: ["org-membros", inst] });
  };
  const saveM = useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: Record<string, unknown> }) => api.put(`/institutions/${inst}/org-nodes/${userId}`, body),
    onSuccess: invalidate,
    onError: (e: unknown) => { const ax = e as { response?: { data?: { message?: string } } }; setErr(ax?.response?.data?.message ?? "Falha ao salvar"); },
  });
  const removeM = useMutation({
    mutationFn: (userId: string) => api.delete(`/institutions/${inst}/org-nodes/${userId}`),
    onSuccess: invalidate,
  });
  const [err, setErr] = useState<string | null>(null);

  const option = useMemo(() => {
    const roots = chartQ.data?.roots ?? [];
    return {
      tooltip: { trigger: "item", formatter: (p: { name?: string; data?: { _depto?: string } }) => `${(p.name ?? "").replace("\n", " — ")}${p.data?._depto ? `<br/>Depto: ${p.data._depto}` : ""}` },
      series: [{
        type: "tree", orient: "TB", top: "4%", bottom: "8%", left: "6%", right: "6%",
        symbol: "circle", symbolSize: 12, roam: true, expandAndCollapse: true, initialTreeDepth: -1,
        label: { position: "bottom", align: "center", verticalAlign: "top", fontSize: 11, lineHeight: 14, distance: 6 },
        lineStyle: { width: 1.2, color: "#90a4ae", curveness: 0.1 },
        itemStyle: { color: "#1976d2", borderColor: "#1976d2" },
        data: [{ name: instName, _depto: null, children: roots.map(toTree), itemStyle: { color: "#455a64" }, label: { fontWeight: "bold", fontSize: 12 } }],
      }],
    };
  }, [chartQ.data, instName]);

  if (!inst) return <Alert severity="info">Selecione uma instituição para ver o organograma.</Alert>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h4">Organograma</Typography>
        {(user?.isMasterAdmin || tenants.length > 1) && (
          <TextField select size="small" label="Instituição" value={inst} onChange={(e) => setPickInst(e.target.value)} sx={{ minWidth: 240 }}>
            {tenants.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        )}
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          {chartQ.isLoading ? <Typography color="text.secondary">Carregando…</Typography>
            : (chartQ.data?.roots.length ?? 0) === 0
              ? <Typography color="text.secondary">Nenhum usuário posicionado no organograma{canEdit ? " — use o painel abaixo para montar a estrutura." : "."}</Typography>
              : <ReactECharts option={option} style={{ height: 560, width: "100%" }} notMerge />}
          {(chartQ.data?.unplaced.length ?? 0) > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Sem posicionamento:</Typography>
              {chartQ.data!.unplaced.map((u) => <Chip key={u.userId} size="small" variant="outlined" label={u.name} />)}
            </Stack>
          )}
        </CardContent>
      </Card>

      {canEdit && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Editar estrutura</Typography>
            {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr(null)}>{err}</Alert>}
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Usuário</TableCell><TableCell>Cargo</TableCell><TableCell>Departamento</TableCell><TableCell>Superior imediato</TableCell><TableCell align="right">Ações</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {(membrosQ.data ?? []).map((m) => (
                  <MembroRow key={m.userId} m={m} membros={membrosQ.data ?? []} cargos={cargosQ.data ?? []} deptos={deptosQ.data ?? []}
                    saving={saveM.isPending} onSave={(body) => { setErr(null); saveM.mutate({ userId: m.userId, body }); }}
                    onRemove={() => removeM.mutate(m.userId)} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function MembroRow({ m, membros, cargos, deptos, saving, onSave, onRemove }: {
  m: Membro; membros: Membro[]; cargos: Cargo[]; deptos: Departamento[]; saving: boolean;
  onSave: (body: Record<string, unknown>) => void; onRemove: () => void;
}) {
  const [cargoId, setCargoId] = useState(m.node?.cargo?.id ?? "");
  const [deptoId, setDeptoId] = useState(m.node?.departamento?.id ?? "");
  const superiorByNode = m.node?.superiorId ? membros.find((x) => x.node?.id === m.node!.superiorId) : null;
  const [superior, setSuperior] = useState<Membro | null>(superiorByNode ?? null);
  const outros = membros.filter((x) => x.userId !== m.userId);

  return (
    <TableRow>
      <TableCell>{m.name}{m.isAdmin && <Chip size="small" label="admin" sx={{ ml: 0.5 }} />}</TableCell>
      <TableCell>
        <TextField select size="small" value={cargoId} onChange={(e) => setCargoId(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value=""><em>—</em></MenuItem>
          {cargos.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
      </TableCell>
      <TableCell>
        <TextField select size="small" value={deptoId} onChange={(e) => setDeptoId(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value=""><em>—</em></MenuItem>
          {deptos.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
      </TableCell>
      <TableCell>
        <Autocomplete<Membro> options={outros} getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.userId === b.userId}
          value={superior} onChange={(_, v) => setSuperior(v)} sx={{ minWidth: 180 }}
          renderInput={(p) => <TextField {...p} size="small" placeholder="Nenhum (raiz)" />} />
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button size="small" variant="contained" disabled={saving}
            onClick={() => onSave({ cargoId: cargoId || null, departamentoId: deptoId || null, superiorUserId: superior?.userId ?? null })}>Salvar</Button>
          {m.node && <Button size="small" color="error" onClick={onRemove}>Remover</Button>}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
